import React, { useEffect } from "react";
import {
  validatePasswordComplexity,
  getPasswordStrengthColors,
  getPasswordStrengthPercentage,
  PASSWORD_COMPLEXITY_CONFIG,
} from "../utils/passwordComplexity.js";

/**
 * Password Strength Indicator Component
 *
 * Provides visual feedback on password complexity requirements
 * including progress bar, requirement checklist, and strength meter
 */
function PasswordStrengthIndicator({
  password,
  confirmPassword = "",
  showDetails = true,
  className = "",
  setPasswordOk,
}) {
  const validation = validatePasswordComplexity(password);
  const colors = getPasswordStrengthColors(validation.strength);
  const percentage = getPasswordStrengthPercentage(
    validation.score,
    validation.maxScore
  );

  // Check if passwords match
  const passwordsMatch =
    password && confirmPassword && password === confirmPassword;

  // Check if all requirements are met
  const allRequirementsMet = validation.isValid && (confirmPassword ? passwordsMatch : true);

  // Call setPasswordOk when requirements change
  useEffect(() => {
    if (setPasswordOk) {
      setPasswordOk(allRequirementsMet);
    }
  }, [allRequirementsMet, setPasswordOk]);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength Meter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Password Strength
          </span>
          <span className={`text-sm font-medium capitalize ${colors.text}`}>
            {validation.strength.replace("-", " ")}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${colors.progress}`}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Score Indicator */}
        <div className="flex items-center space-x-1">
          <span className="text-xs text-slate-500 dark:text-slate-400 mr-2">
            {validation.isValid
              ? "All requirements met"
              : `${validation.score}/${validation.maxScore} requirements met`}
          </span>
          {Array.from({ length: validation.maxScore }, (_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                i >= validation.score
                  ? "bg-slate-300 dark:bg-slate-600"
                  : colors.indicator
              }`}
            />
          ))}
        </div>
      </div>

      {/* Detailed Requirements */}
      {showDetails && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Requirements:
          </h4>

          <div className="space-y-3">
            {/* Minimum Length */}
            <div className="flex items-center space-x-2">
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                  validation.requirements.minLength.met
                    ? "bg-green-100 dark:bg-green-900/20"
                    : "bg-slate-100 dark:bg-slate-700"
                }`}
              >
                {validation.requirements.minLength.met && (
                  <svg
                    className="w-2.5 h-2.5 text-green-600 dark:text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <span
                className={`text-sm ${
                  validation.requirements.minLength.met
                    ? "text-green-600 dark:text-green-400"
                    : "text-slate-600 dark:text-slate-400"
                }`}
              >
                At least {validation.requirements.minLength.required} characters
                ({validation.requirements.minLength.actual}/
                {validation.requirements.minLength.required})
              </span>
            </div>

            {/* Character Categories Requirement */}
            <div className="flex items-center space-x-2">
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                  Object.values(validation.requirements.categories).filter(
                    (cat) => cat.met
                  ).length >= 3
                    ? "bg-green-100 dark:bg-green-900/20"
                    : "bg-slate-100 dark:bg-slate-700"
                }`}
              >
                {Object.values(validation.requirements.categories).filter(
                  (cat) => cat.met
                ).length >= 3 && (
                  <svg
                    className="w-2.5 h-2.5 text-green-600 dark:text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <span
                className={`text-sm ${
                  Object.values(validation.requirements.categories).filter(
                    (cat) => cat.met
                  ).length >= 3
                    ? "text-green-600 dark:text-green-400"
                    : "text-slate-600 dark:text-slate-400"
                }`}
              >
                At least 3 of the 4 character types (
                {
                  Object.values(validation.requirements.categories).filter(
                    (cat) => cat.met
                  ).length
                }
                /3)
              </span>
            </div>

            {/* Character Categories Details */}
            <div className="ml-6 space-y-1">
              {Object.entries(validation.requirements.categories).map(
                ([key, requirement]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full flex items-center justify-center ${
                        requirement.met
                          ? "bg-green-100 dark:bg-green-900/20"
                          : "bg-slate-100 dark:bg-slate-700"
                      }`}
                    >
                      {requirement.met && (
                        <svg
                          className="w-1.5 h-1.5 text-green-600 dark:text-green-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <span
                      className={`text-xs ${
                        requirement.met
                          ? "text-green-600 dark:text-green-400"
                          : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {requirement.label}
                    </span>
                  </div>
                )
              )}
            </div>

             {/* Password Match Requirement */}
             <div className="flex items-center space-x-2">
               <div
                 className={`w-4 h-4 rounded-full flex items-center justify-center ${
                   passwordsMatch
                     ? "bg-green-100 dark:bg-green-900/20"
                     : "bg-red-100 dark:bg-red-900/20"
                 }`}
               >
                 {passwordsMatch ? (
                   <svg
                     className="w-2.5 h-2.5 text-green-600 dark:text-green-400"
                     fill="currentColor"
                     viewBox="0 0 20 20"
                   >
                     <path
                       fillRule="evenodd"
                       d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                       clipRule="evenodd"
                     />
                   </svg>
                 ) : (
                   <svg
                     className="w-2.5 h-2.5 text-red-600 dark:text-red-400"
                     fill="currentColor"
                     viewBox="0 0 20 20"
                   >
                     <path
                       fillRule="evenodd"
                       d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                       clipRule="evenodd"
                     />
                   </svg>
                 )}
               </div>
               <span
                 className={`text-sm ${
                   passwordsMatch
                     ? "text-green-600 dark:text-green-400"
                     : "text-red-600 dark:text-red-400"
                 }`}
               >
                 Passwords match
               </span>
             </div>
          </div>

          {/* Summary */}
          
        </div>
      )}
    </div>
  );
}

export default PasswordStrengthIndicator;
